import { QueryClient } from '@tanstack/react-query';

import { DoctorPartnershipStatus, GetHospitalByIdQuery } from '@/types/bff/gql-schema';
import { HospitalResponse } from '@/types/hospital';

import { exhaustiveGuard } from '@/shared/utils/typeGuard';
import { useGetHospitalByIdQuery } from './faceToFace.gql.impl';

interface GetHospital {
  data?: HospitalResponse;
  refetch: () => void;
  isLoading: boolean;
}

export enum EDoctorPartnershipStatus {
  Affiliated = 'AFFILIATED',
  Applied = 'APPLIED',
  Applying = 'APPLYING',
  Approved = 'APPROVED',
  Blocked = 'BLOCKED',
  NotAffiliated = 'NOT_AFFILIATED',
  Rejected = 'REJECTED',
  Revoked = 'REVOKED',
  Unknown = 'UNKNOWN',
}

const convertDoctorPartnershipStatus = (partnershipStatus: DoctorPartnershipStatus): EDoctorPartnershipStatus => {
  try {
    switch (partnershipStatus) {
      case DoctorPartnershipStatus.Affiliated:
        return EDoctorPartnershipStatus.Affiliated;
      case DoctorPartnershipStatus.Applied:
        return EDoctorPartnershipStatus.Applied;
      case DoctorPartnershipStatus.Applying:
        return EDoctorPartnershipStatus.Applying;
      case DoctorPartnershipStatus.Approved:
        return EDoctorPartnershipStatus.Approved;
      case DoctorPartnershipStatus.Blocked:
        return EDoctorPartnershipStatus.Blocked;
      case DoctorPartnershipStatus.NotAffiliated:
        return EDoctorPartnershipStatus.NotAffiliated;
      case DoctorPartnershipStatus.Rejected:
        return EDoctorPartnershipStatus.Rejected;
      case DoctorPartnershipStatus.Revoked:
        return EDoctorPartnershipStatus.Revoked;
      default:
        return exhaustiveGuard(partnershipStatus);
    }
  } catch {
    return EDoctorPartnershipStatus.Unknown;
  }
};

const useGetHospitalById = (hospitalPid: string): GetHospital => {
  const { data, isLoading, refetch } = useGetHospitalByIdQuery(
    {
      publicId: hospitalPid,
    },
    {
      select: convertToHospitalResponse,
      staleTime: 1000 * 60 * 5,
    }
  );
  return { data, refetch, isLoading };
};

export default useGetHospitalById;

const prefetchQuery = async (queryClient: QueryClient, hospitalPid: string) => {
  const params = { publicId: hospitalPid };

  await queryClient.prefetchQuery(useGetHospitalByIdQuery.getKey(params), useGetHospitalByIdQuery.fetcher(params));
};

useGetHospitalById.prefetchQuery = prefetchQuery;

const convertToHospitalResponse = (data: GetHospitalByIdQuery): HospitalResponse => {
  return {
    ...data.getHospitalByPublicId,
    topRatedDoctorReviews: data.getHospitalByPublicId.topRatedDoctorReviews.map((review) => ({
      ...review,
      comments: review.comments.map((comment) => ({
        ...comment,
        reviewCommentHolder: {
          ...comment.reviewCommentHolder,
          profileImageUrl: comment.reviewCommentHolder.profileImageUrl ?? '',
        },
      })),
    })),
    doctors: data.getHospitalByPublicId.doctors.map((doctor) => ({
      ...doctor,
      partnershipStatus: convertDoctorPartnershipStatus(doctor.partnershipStatus),
    })),
  };
};

const getUseGetHospitalByIdQueryKey = (hospitalPid: string) => {
  return useGetHospitalByIdQuery.getKey({ publicId: hospitalPid });
};

useGetHospitalById.getKey = getUseGetHospitalByIdQueryKey;
